import {
  usePaymentRestrictions,
  type UsePaymentRestrictionsResult,
} from './usePaymentRestrictions'

type ListingPaymentRestrictions = UsePaymentRestrictionsResult & {
  isSellerCollects: boolean
  isCreditCardBlockedByAccount: boolean
  isCreditCardDisabledByListing: boolean
}

/**
 * Extends account-level payment restrictions with listing-level constraints.
 *
 * Use this instead of `usePaymentRestrictions` anywhere inside the bidding flow
 * where a listing's own payment configuration must be respected.
 */
export const useListingPaymentRestrictions = (
  listingIsCreditCardEnabled?: boolean,
  listingSellerCollects?: boolean
): ListingPaymentRestrictions => {
  const accountRestrictions = usePaymentRestrictions()
  // On a Seller Collects listing the buyer pays the seller for the goods, so
  // none of B-Stock's own methods apply regardless of account-level state.
  const isSellerCollects = listingSellerCollects === true
  // Keep account-level CC blocks separate from listing-level CC disable:
  // account block -> "Restricted"
  // listing disable -> gray out only
  const isCreditCardBlockedByAccount = accountRestrictions.isCreditCardBlocked
  const isCreditCardDisabledByListing = listingIsCreditCardEnabled !== true

  if (isSellerCollects) {
    return {
      ...accountRestrictions,
      isSellerCollects,
      isACHBlocked: true,
      isNetTermsBlocked: true,
      isWireFallbackAvailable: false,
      isCreditCardBlockedByAccount,
      isCreditCardDisabledByListing,
      isCreditCardBlocked: true,
    }
  }

  return {
    ...accountRestrictions,
    isSellerCollects,
    isCreditCardBlockedByAccount,
    isCreditCardDisabledByListing,
    isCreditCardBlocked:
      isCreditCardBlockedByAccount || isCreditCardDisabledByListing,
  }
}
